import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, BookOpen, Download, Loader2, MailCheck, Search, Wrench, TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { MaskedLines, Reveal } from "../components/motion";
import { articles } from "../data/content";
import { api } from "../lib/api";
import { usePageTitle } from "../lib/title";

const pillars = [
  {
    icon: Search,
    title: "How It Works",
    body: "Deductibles, copays, coinsurance, and the numbers that actually decide what you pay — explained once, in plain English.",
  },
  {
    icon: Wrench,
    title: "How to Use It",
    body: "Getting full value from the coverage you already have — networks, HSAs, enrollment windows, and the mistakes to avoid.",
  },
  {
    icon: TrendingUp,
    title: "How to Optimize It",
    body: "Smarter plan design for families and employers — including the alternative care models most people never hear about.",
  },
];

export default function BenefitsDecoded() {
  const [form, setForm] = useState({ name: "", email: "" });
  usePageTitle("Benefits Decoded — Health Insurance Education Series");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const featured = articles.slice(0, 4);
  const guide = articles.find((a) => a.pdf);

  const subscribe = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", {
        type: "newsletter",
        name: form.name,
        email: form.email,
        message: "Benefits Decoded — new subscriber",
      });
      setDone(true);
      toast.success("You're on the list — welcome to Benefits Decoded.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="benefits-decoded-page">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-36 lg:px-10 lg:pt-44" data-testid="bd-hero">
        <h1 className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="bd-kicker">
          Benefits Decoded: Health Insurance Explained
        </h1>
        <MaskedLines
          as="p"
          className="mt-8 font-serif text-5xl font-light leading-[0.95] tracking-tighter text-forest sm:text-7xl lg:text-8xl"
          lines={[
            "Benefits,",
            <span key="l2">
              <em className="text-terra">decoded</em>.
            </span>,
          ]}
        />
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">
            Health insurance, translated. Every installment takes one confusing corner of the benefits world and
            makes it make sense — how it works, how to use it, and how to optimize it. No jargon. No pitch.
            Just understanding you can act on.
          </p>
        </Reveal>
      </section>

      {/* PILLARS */}
      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10" data-testid="bd-pillars">
        <div className="grid gap-6 md:grid-cols-3">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={0.08 * i}>
              <div
                className="h-full border border-border p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60 hover:bg-sand/50"
                data-testid={`bd-pillar-${i}`}
              >
                <p.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif text-2xl font-medium tracking-tight text-forest">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink/65">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FREE GUIDE */}
      {guide && (
        <section className="border-y border-border bg-sand/70" data-testid="bd-guide">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-6 py-14 lg:px-10">
            <Reveal className="max-w-2xl">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra">Free Guide</p>
              <h2 className="mt-4 font-serif text-3xl font-light leading-tight tracking-tight text-forest sm:text-4xl">
                {guide.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-ink/65">{guide.excerpt}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <a
                href={guide.pdf}
                download
                data-testid="bd-guide-download"
                className="group inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-moss"
              >
                <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                Download the PDF
              </a>
            </Reveal>
          </div>
        </section>
      )}

      {/* LATEST FROM THE SERIES */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="bd-articles">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra">From the Series</p>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
              Start <em className="text-terra">decoding</em>
            </h2>
          </div>
          <Link
            to="/education"
            data-testid="bd-view-all-link"
            className="link-underline text-sm font-medium text-forest hover:text-terra"
          >
            Browse the full Knowledge Field
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {featured.map((a, i) => (
            <Reveal key={a.slug} delay={0.06 * i}>
              <Link
                to={`/education/${a.slug}`}
                data-testid={`bd-article-${a.slug}`}
                className="group flex h-full flex-col justify-between border border-border p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60 hover:bg-sand/50"
              >
                <div>
                  <div className="flex items-center gap-4">
                    <BookOpen className="h-4 w-4 text-gold" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">
                      {a.category} — {a.readTime}
                    </span>
                  </div>
                  <h3 className="mt-5 font-serif text-2xl font-medium leading-snug tracking-tight text-forest transition-colors group-hover:text-terra">
                    {a.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-ink/65">{a.excerpt}</p>
                </div>
                <ArrowUpRight className="mt-8 h-5 w-5 text-forest/40 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SUBSCRIBE */}
      <section className="relative overflow-hidden bg-forest" data-testid="bd-subscribe">
        <div className="relative mx-auto grid max-w-7xl gap-10 px-6 py-24 lg:grid-cols-12 lg:px-10 lg:py-28">
          <Reveal className="lg:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold">Never Miss a Decode</p>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl">
              Get each new installment <em className="text-gold">in your inbox</em>.
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-cream/70">
              Leave your email and every new Benefits Decoded piece lands with you first. No noise, no spam —
              just the next thing worth understanding.
            </p>
          </Reveal>
          <Reveal className="flex items-center lg:col-span-5 lg:col-start-8" delay={0.1}>
            {done ? (
              <div className="flex items-center gap-4 border border-gold/50 bg-gold/10 p-8" data-testid="bd-subscribe-success">
                <MailCheck className="h-8 w-8 shrink-0 text-gold" strokeWidth={1.5} />
                <p className="font-serif text-2xl font-light leading-snug text-cream">
                  You&rsquo;re in. Watch your inbox for the next decode.
                </p>
              </div>
            ) : (
              <form onSubmit={subscribe} className="w-full space-y-4" data-testid="bd-subscribe-form">
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Your name"
                  className="rounded-none border-cream/25 bg-cream/10 px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus-visible:ring-1 focus-visible:ring-gold focus-visible:ring-offset-0"
                  data-testid="bd-name-input"
                />
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="Your email"
                  className="rounded-none border-cream/25 bg-cream/10 px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus-visible:ring-1 focus-visible:ring-gold focus-visible:ring-offset-0"
                  data-testid="bd-email-input"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="bd-subscribe-button"
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-medium text-forest transition-all duration-300 hover:-translate-y-0.5 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Joining…
                    </>
                  ) : (
                    "Join Benefits Decoded"
                  )}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
